import React, { useState, useEffect, useCallback } from 'react'
import { Auth } from 'aws-amplify'
import { isAdmin, isMultipleOrganization, isPlatformAdmin, getUserData } from '../../../Utils/ServerUtil'
import { FTCheckbox } from '../../FTCheckbox'
import Menu from '@material-ui/core/Menu'
import { List, Divider, TextField, Autocomplete, Chip, Box } from '@mui/material'
import eventCapture from '../../../eventList'
import { posthogCapture } from '../../../PostHogFunction'
import { makeStyles } from '@material-ui/core/styles'
import NotFeature from '../../../fhg/security/NotFeature'
import { CUSTOMER_ENV } from '../../../Utils/ServerUtil'
import { FTButtonIconText } from '../../buttons'
import { FTFilterIcon } from '../../icons'
import { useEmployeesStore } from '../../zustandState/ZustandStore'
import { useMenuStore } from '../../zustandState/ZustandStore'
import config from '../../../config/runtime'

const useStyles = makeStyles((theme) => ({
    mouseTracker: {
        '&:focus': {
            outline: 'none',
        },
    },
    menuContent: {
        padding: '16px',
        minWidth: '350px',
    },
    autocompleteWrapper: {
        marginBottom: '12px',
        '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
                borderColor: '#4a287d',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#4a287d',
            },
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#4a287d',
        },
    },
}))

function EmployeesFilterMenu() {
    const classes = useStyles()
    const { SelectedEmployeesState, setSelectedEmployeesState } = useEmployeesStore()
    const { MenuClosedState, setMenuClosedState } = useMenuStore()

    const [EMPLOYEES_NAMES, setEMPLOYEES_NAMES] = useState([])
    const [selectedEmployees, setSelectedEmployees] = useState([])
    const [selectAllEmployees, setSelectAllEmployees] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)
    const [isInitialLoad, setIsInitialLoad] = useState(true)

    // Fetch employee names on component mount only
    useEffect(() => {
        fetchEmployeeNames()
    }, [])

    // Initialize component without localStorage due to security concerns
    // NOTE: localStorage retrieval removed - sensitive employee data should not be stored/retrieved from localStorage
    useEffect(() => {
        // If persistence is needed, consider implementing server-side storage or encrypted storage
        setIsInitialLoad(false)
    }, [EMPLOYEES_NAMES])

    // Sync with Zustand state when local state changes
    useEffect(() => {
        if (!isInitialLoad) {
            setSelectedEmployeesState(selectedEmployees)
        }
    }, [selectedEmployees, setSelectedEmployeesState, isInitialLoad])

    // Update menu closed state
    useEffect(() => {
        setMenuClosedState(!menuOpen)
    }, [menuOpen, setMenuClosedState])

    // Handle menu toggle
    const handleEmployeeMenuToggle = (event) => {
        if (menuOpen) {
            // Menu is closing - this is where you'd handle any action needed on close
            setAnchorEl(null)

            // NOTE: localStorage storage removed for security - sensitive employee data should not be stored in plain text
            // If persistence is needed, consider using encrypted storage or server-side storage

            // Log the event
            //console.log('Menu closed with selected employees:', selectedEmployees)
        } else {
            // Menu is opening
            setAnchorEl(event.currentTarget)
        }
        setMenuOpen(!menuOpen)
    }

    // Handle selection of individual employee
    const handleEmployeesSelect = useCallback((employeeName) => {
        posthogCapture(eventCapture.FILTERED_BY_EMPLOYEES)

        setSelectedEmployees((prevSelectedEmployees) => {
            const updatedEmployees = prevSelectedEmployees.includes(employeeName)
                ? prevSelectedEmployees.filter((name) => name !== employeeName)
                : [...prevSelectedEmployees, employeeName]

            // Update selectAll state based on selection
            if (updatedEmployees.length === EMPLOYEES_NAMES.length) {
                setSelectAllEmployees(true)
            } else {
                setSelectAllEmployees(false)
            }

            return updatedEmployees
        })
    }, [EMPLOYEES_NAMES.length])

    // Handle select all checkbox
    const handleSelectAllChange = useCallback(() => {
        const newSelectAllEmployees = !selectAllEmployees

        if (newSelectAllEmployees) {
            // Select all employees
            setSelectedEmployees([...EMPLOYEES_NAMES])
        } else {
            // Deselect all employees
            setSelectedEmployees([])
        }

        setSelectAllEmployees(newSelectAllEmployees)
    }, [selectAllEmployees, EMPLOYEES_NAMES])

    // Fetch employee names list
    const fetchEmployeeNames = async () => {
        // Check if user is platform admin before making the API call
        const userData = getUserData()
        if (!userData || !userData.isPlatformAdmin) {
            // User is not platform admin, skipping employees fetch
            return
        }

        let jwtToken

        try {
            const session = await Auth.currentSession()
            jwtToken = session.idToken.jwtToken
        } catch (e) {
            try {
                const session = await Auth.currentSession()
                jwtToken = session.idToken.jwtToken
            } catch (e) {
                // Error refreshing token
                return; // Exit if we can't get a token
            }
        }

        const headers = {
            authorization: jwtToken,
            accept: 'application/json',
        }

        if (isAdmin() || isMultipleOrganization()) {
            const organization = localStorage.getItem('hw.' + window.btoa('organization'))
            if (organization && window.atob(organization)) {
                headers['HW-Organization'] = window.atob(organization) ? window.atob(organization) : 'f376c401-6295-4038-a146-e8b52eef6385';
            }
        }

        const options = {
            method: 'GET',
            headers: headers,
        }

        const url = `${config.API_ENDPOINT_V3}/employees`

        try {
            const response = await fetch(url, options)

            if (response.ok) {
                const employeeData = await response.json()
                const employeeNames = employeeData.map(employee => employee.name);

                setEMPLOYEES_NAMES(employeeNames)
                // Employee names loaded successfully
            } else {
                // API request failed with status: response.status
            }
        } catch (error) {
            // Error occurred during API request
        }
    }

    return (
        <div>
            <NotFeature feature={CUSTOMER_ENV}>
                <FTButtonIconText
                    id="employee-filter-button"
                    title="evalDashboard.employees.column"
                    buttonAction={handleEmployeeMenuToggle}
                    icon={
                        <FTFilterIcon
                            activeFilter={selectedEmployees.length > 0 && selectedEmployees.length < EMPLOYEES_NAMES.length}
                        />
                    }
                />
                <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleEmployeeMenuToggle}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    getContentAnchorEl={null}
                >
                    <div className={classes.menuContent}>
                        <div className={classes.autocompleteWrapper}>
                            <Autocomplete
                                multiple
                                id="employee-autocomplete"
                                options={EMPLOYEES_NAMES}
                                value={selectedEmployees}
                                onChange={(event, newValue) => {
                                    posthogCapture(eventCapture.FILTERED_BY_EMPLOYEES)
                                    setSelectedEmployees(newValue)
                                    
                                    // Update selectAll state based on selection
                                    if (newValue.length === EMPLOYEES_NAMES.length) {
                                        setSelectAllEmployees(true)
                                    } else {
                                        setSelectAllEmployees(false)
                                    }
                                }}
                                disableCloseOnSelect
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Search and select employees"
                                        placeholder="Type to search..."
                                        size="small"
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props}>
                                        <FTCheckbox
                                            checked={selectedEmployees.includes(option)}
                                            style={{ 
                                                marginRight: 8,
                                                color: selectedEmployees.includes(option) ? '#4a287d' : undefined 
                                            }}
                                            sx={selectedEmployees.includes(option) ? {
                                                '& .MuiSvgIcon-root': { 
                                                    color: '#4a287d'
                                                },
                                                '&.Mui-checked': {
                                                    color: '#4a287d',
                                                },
                                            } : {}}
                                        />
                                        {option}
                                    </li>
                                )}
                                style={{ width: '100%' }}
                            />
                        </div>
                        
                        <Divider />
                        
                        <Box sx={{ mt: 1, mb: 1 }}>
                            <FTCheckbox
                                key="Select All"
                                value="Select All"
                                onClick={handleSelectAllChange}
                                checked={selectAllEmployees}
                                style={{ color: selectAllEmployees ? '#4a287d' : undefined }}
                                sx={{
                                    '&.Mui-checked': {
                                        color: '#4a287d',
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: '#4a287d',
                                    },
                                }}
                            />
                        </Box>
                    </div>
                </Menu>
            </NotFeature>
        </div>
    )
}

export default EmployeesFilterMenu
